import type { KeyboardEvent, MouseEvent } from 'react';
import type { BadgeOffer } from '@/queries/bundles/bundleOffers.schema';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';

interface Props {
  badge?: BadgeOffer;
  isVip?: boolean;
  onClick?: (e?: MouseEvent<HTMLButtonElement>) => void;
  dailyBonusText?: string;
}

export const BundleBadge = ({
  isVip,
  badge,
  onClick,
  dailyBonusText,
}: Props) => {
  const t = useTranslations('bundles');
  const isInfo = !isVip && !badge;

  const notVipBadge = isInfo ? dailyBonusText : badge?.label;

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClick?.();
    }
  };

  const badgeStyles = badge?.styles?.cssStyles;
  const stroke = badge?.styles?.stroke;

  return (
    <button
      onClick={onClick}
      onKeyDown={handleKeyDown}
      style={badgeStyles}
      className={cn(
        'flex relative items-center font-medium gap-1 rounded-full px-2 py-0.5',
        isVip && 'bg-vip-bundle-badge text-c900 font-semibold',
        isInfo && 'bg-c900/40 glass-effect backdrop-blur-sm drop-shadow-[1px_2px_10px_rgba(0,0,0,0.55)]',
      )}
    >
      {stroke && (
        <span
          aria-hidden="true"
          className="pointer-events-none block absolute inset-0 rounded-full"
          style={{
            background: stroke.gradient,
            padding: stroke.width,
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />
      )}
      <span className={cn('text-xs block leading-4 whitespace-nowrap tracking-[0.005rem]', !isVip && isInfo && 'tracking-[0.01em]',
      )}
      >
        {
          isVip
            ? t('vip-badge')
            : notVipBadge
        }
      </span>
      {isInfo && <Icon name="info" className="h-3 w-3 min-w-3 opacity-50" />}
    </button>
  );
};
